import { useState, useCallback, useEffect, useRef } from "react";
import { Socket } from "socket.io-client";
import {
  isMusicEnabled,
  setMusicEnabled,
} from "../../../game/systems/SoundManager";
import { WorldEvent } from "../../WorldEventNotification";
import type {
  HarvestRewardData,
  AmbientTradeItem,
  Cooldown,
  RiftStormData,
  PirateRaidData,
} from "../types";

interface UseGameCanvasStateProps {
  savedStats?: { berries: number; level: number; xp: number; infamy: number };
  socketRef: React.MutableRefObject<Socket | null>;
}

export function useGameCanvasState({
  savedStats,
  socketRef,
}: UseGameCanvasStateProps) {
  // Track if we've initialized to prevent resetting earned berries when playerInfo updates
  const initializedRef = useRef<boolean>(false);

  // Player state
  const [playerId, setPlayerId] = useState<string | undefined>(undefined);
  const [shipCount, setShipCount] = useState<number>(0);
  const [position, setPosition] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [currentRegion, setCurrentRegion] = useState<string>("east_blue");
  const [tilePos, setTilePos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [chunkPos, setChunkPos] = useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [securityTier, setSecurityTier] = useState<
    "safe_zone" | "contested" | "open_waters"
  >("contested");
  const [playerHp, setPlayerHp] = useState<{
    hull: number;
    maxHull: number;
    sail: number;
    maxSail: number;
  }>({ hull: 100, maxHull: 100, sail: 50, maxSail: 50 });
  const [playerSP, setPlayerSP] = useState<{ current: number; max: number }>({
    current: 100,
    max: 100,
  });
  const [inCombat, setInCombat] = useState<boolean>(false);
  const [pieces, setPieces] = useState<number>(savedStats?.berries ?? 0);
  const [xp, setXp] = useState<number>(savedStats?.xp ?? 0);
  const [level, setLevel] = useState<number>(savedStats?.level ?? 1);
  const [infamy, setInfamy] = useState<number>(savedStats?.infamy ?? 0);

  // Mark as initialized on first render to prevent resetting earned berries
  useEffect(() => {
    initializedRef.current = true;
  }, []);
  const [connected, setConnected] = useState<boolean>(false);
  const [unlockedBlueprints, setUnlockedBlueprints] = useState<Set<string>>(
    new Set(),
  );
  const [shipId, setShipId] = useState<string>("");
  const [shipType, setShipType] = useState<string>("sloop");

  // Hotbar and inventory state
  const [socketForHotbar, setSocketForHotbar] = useState<Socket | null>(null);
  const [inventoryItems, setInventoryItems] = useState<
    Array<{
      itemId: string;
      quantity: number;
      definition?: {
        id: string;
        name: string;
        icon: string;
        description?: string;
        type?: string;
        rarity?: string;
      } | null;
    }>
  >([]);
  const [hotbarVisible, setHotbarVisible] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("hotbarVisible");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  });

  // Panel states
  const [inventoryOpen, setInventoryOpen] = useState<boolean>(false);
  const [shopOpen, setShopOpen] = useState<boolean>(false);
  const [currentTradingPost, setCurrentTradingPost] =
    useState<string>("Trading Post");
  const [shipyardOpen, setShipyardOpen] = useState<boolean>(false);
  const [currentShipyard, setCurrentShipyard] = useState<string>("Shipyard");
  const [fleetPanelOpen, setFleetPanelOpen] = useState<boolean>(false);
  const [expressionPanelOpen, setExpressionPanelOpen] =
    useState<boolean>(false);
  const [debugPanelOpen, setDebugPanelOpen] = useState<boolean>(false);
  const [moderatorPanelOpen, setModeratorPanelOpen] = useState<boolean>(false);
  const [researchPanelOpen, setResearchPanelOpen] = useState<boolean>(false);
  const [researchIslandId, setResearchIslandId] = useState<string | null>(null);
  const [researchIslandPosition, setResearchIslandPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [researchRefreshNonce, setResearchRefreshNonce] = useState<number>(0);
  const [tavernOpen, setTavernOpen] = useState<boolean>(false);
  const [bankOpen, setBankOpen] = useState<boolean>(false);
  const [questLogOpen, setQuestLogOpen] = useState<boolean>(false);
  const [questsPanelOpen, setQuestsPanelOpen] = useState<boolean>(false);
  const [questJournalOpen, setQuestJournalOpen] = useState<boolean>(false);
  const [selectedQuestForJournal, setSelectedQuestForJournal] = useState<
    string | null
  >(null);
  const [trackedQuestId, setTrackedQuestId] = useState<string | null>(null);
  const [inboxOpen, setInboxOpen] = useState<boolean>(false);
  const [ambientTradeOpen, setAmbientTradeOpen] = useState<boolean>(false);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState<boolean>(false);
  const [bottleModalOpen, setBottleModalOpen] = useState<boolean>(false);
  const [shipInvestigationOpen, setShipInvestigationOpen] =
    useState<boolean>(false);
  const [fishingResearchOpen, setFishingResearchOpen] =
    useState<boolean>(false);
  const [resourceResearchOpen, setResourceResearchOpen] =
    useState<boolean>(false);
  const [lootOpen, setLootOpen] = useState<boolean>(false);
  const [worldHistoryOpen, setWorldHistoryOpen] = useState<boolean>(false);
  const [infamyInfoOpen, setInfamyInfoOpen] = useState<boolean>(false);
  const [reputationOpen, setReputationOpen] = useState<boolean>(false);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [skillsOpen, setSkillsOpen] = useState<boolean>(false);
  const [fittingOpen, setFittingOpen] = useState<boolean>(false);
  const [craftingOpen, setCraftingOpen] = useState<boolean>(false);
  const [cookingOpen, setCookingOpen] = useState<boolean>(false);
  const [smithingOpen, setSmithingOpen] = useState<boolean>(false);
  const [alchemyOpen, setAlchemyOpen] = useState<boolean>(false);
  const [constructionHotbarActive, setConstructionHotbarActive] =
    useState<boolean>(false);
  const [currentConstructionKitId, setCurrentConstructionKitId] = useState<
    string | null
  >(null);
  const [territoryClaimOpen, setTerritoryClaimOpen] = useState<boolean>(false);
  const [siegePanelOpen, setSiegePanelOpen] = useState<boolean>(false);
  const [territoryManagementOpen, setTerritoryManagementOpen] =
    useState<boolean>(false);
  const [whirlpoolResearchOpen, setWhirlpoolResearchOpen] =
    useState<boolean>(false);
  const [riftScarData, setRiftScarData] = useState<any>(null);
  const [signaturePanelOpen, setSignaturePanelOpen] = useState<boolean>(false);
  const [optionsOpen, setOptionsOpen] = useState<boolean>(false);
  const [referralPanelOpen, setReferralPanelOpen] = useState<boolean>(false);
  const [dailyRewardsPanelOpen, setDailyRewardsPanelOpen] =
    useState<boolean>(false);
  const [courierPanelOpen, setCourierPanelOpen] = useState<boolean>(false);
  const [riftRelicPanelOpen, setRiftRelicPanelOpen] = useState<boolean>(false);
  const [dockServicesPanelOpen, setDockServicesPanelOpen] =
    useState<boolean>(false);
  const [currentShipHeat, setCurrentShipHeat] = useState<number>(0);
  const [maxShipHeat, setMaxShipHeat] = useState<number>(100);

  // Docking state
  const [isDocked, setIsDocked] = useState<boolean>(false);
  const [dockedIslandId, setDockedIslandId] = useState<string | null>(null);
  const [dockedIslandName, setDockedIslandName] =
    useState<string>("Unknown Island");
  const [dockedIslandHasShop, setDockedIslandHasShop] =
    useState<boolean>(false);
  const [dockedIslandHasShipyard, setDockedIslandHasShipyard] =
    useState<boolean>(false);
  const [dockedIslandIsHarborOffice, setDockedIslandIsHarborOffice] =
    useState<boolean>(false);
  const [dockedIslandHasTavern, setDockedIslandHasTavern] =
    useState<boolean>(false);
  const [dockedIslandHasCourier, setDockedIslandHasCourier] =
    useState<boolean>(false);
  const [dockedIslandHasDockServices, setDockedIslandHasDockServices] =
    useState<boolean>(false);
  const [dockedIslandIsPermanent, setDockedIslandIsPermanent] =
    useState<boolean>(false);
  const [dockPrompt, setDockPrompt] = useState<{
    islandId: string;
    islandName: string;
  } | null>(null);
  const [dockedIslandNpcs, setDockedIslandNpcs] = useState<
    Array<{
      id: string;
      name: string;
      title: string;
      portrait: string;
      dialogue?: { greeting: string; farewell: string };
      questStatus?: "available" | "active" | "complete" | "none";
      questId?: string;
    }>
  >([]);

  // UI state
  const [toastQueue, setToastQueue] = useState<
    Array<{ id: string; text: string; type: "error" | "warning" | "success" }>
  >([]);
  const [worldEvents, setWorldEvents] = useState<WorldEvent[]>([]);
  const [combatCooldowns, setCombatCooldowns] = useState<Cooldown[]>([]);
  const [kickMessage, setKickMessage] = useState<string | null>(null);
  const [isTypingInInput, setIsTypingInInput] = useState<boolean>(false);
  const [isDisconnected, setIsDisconnected] = useState<boolean>(false);
  const [reconnectAttempt, setReconnectAttempt] = useState<number>(0);
  const [duplicateConnectionError, setDuplicateConnectionError] =
    useState<boolean>(false);

  // Weather state
  const [currentWeather, setCurrentWeather] = useState<
    "clear" | "fog" | "storm" | "calm"
  >("clear");

  // Research/Interaction state
  const [fishingSpotId, setFishingSpotId] = useState<string | null>(null);
  const [fishingSpotType, setFishingSpotType] = useState<string | null>(null);
  const [fishingSpotPosition, setFishingSpotPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [resourceNodeId, setResourceNodeId] = useState<string | null>(null);
  const [resourceNodeType, setResourceNodeType] = useState<string | null>(null);
  const [resourceNodePosition, setResourceNodePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [lootContainerId, setLootContainerId] = useState<string | null>(null);
  const [whirlpoolId, setWhirlpoolId] = useState<string | null>(null);
  const [whirlpoolPosition, setWhirlpoolPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [siegeIslandId, setSiegeIslandId] = useState<string>("");
  const [siegeIslandName, setSiegeIslandName] = useState<string>("");
  const [managementIslandId, setManagementIslandId] = useState<string>("");
  const [managementIslandName, setManagementIslandName] = useState<string>("");
  const [managementIsOwner, setManagementIsOwner] = useState<boolean>(false);
  const [territoryStatusOpen, setTerritoryStatusOpen] =
    useState<boolean>(false);
  const [statusIslandId, setStatusIslandId] = useState<string>("");
  const [statusIslandName, setStatusIslandName] = useState<string>("");
  const [claimIslandData, setClaimIslandData] = useState<{
    id: string;
    name: string;
    type: "tiny" | "small" | "medium" | "large" | "massive";
    biome: string;
    isPermanent: boolean;
    isClaimed: boolean;
    ownerName: string | null;
    ownerType:
      | "player"
      | "crew"
      | "faction_regime"
      | "faction_navy"
      | "faction_pirate"
      | "faction_merchant"
      | "faction_smuggler"
      | null;
    customName: string | null;
  } | null>(null);
  const [investigatedShip, setInvestigatedShip] = useState<{
    shipId: string;
    name?: string;
    ownerId?: string;
    ownerName?: string;
    fleetTag?: string | null;
    titleName?: string | null;
    isNPC?: boolean;
    level?: number;
    hp?: number;
    maxHp?: number;
    faction?: string;
    subfaction?: string;
    defense?: number;
    damage?: number;
  } | null>(null);
  const [bottleContent, setBottleContent] = useState<{
    type: string;
    content: string;
    reward?: {
      pieces?: number;
      itemId?: string;
      itemName?: string;
      treasureX?: number;
      treasureY?: number;
    };
  } | null>(null);
  const [npcDialogueOpen, setNpcDialogueOpen] = useState<boolean>(false);
  const [npcDialogueData, setNpcDialogueData] = useState<{
    npcId: string;
    npcName: string;
    npcTitle: string;
    npcPortrait: string;
    dialogue: string;
    questDialogue?: {
      start?: string;
      progress?: string;
      complete?: string;
    };
    questProgress?: number;
  } | null>(null);
  const [ambientTradeData, setAmbientTradeData] = useState<{
    ambientId: string;
    traderName: string;
    items: AmbientTradeItem[];
  } | null>(null);
  const [harvestProgress, setHarvestProgress] = useState<{
    nodeId: string;
    nodeName: string;
    harvestTimeSeconds: number;
    startedAt: number;
  } | null>(null);
  const [harvestReward, setHarvestReward] = useState<HarvestRewardData | null>(
    null,
  );

  // Loading overlay state
  const [loadingOverlayVisible, setLoadingOverlayVisible] =
    useState<boolean>(false);
  const [loadingOverlayMessage, setLoadingOverlayMessage] =
    useState<string>("Loading...");
  const [isInstanceEntry, setIsInstanceEntry] = useState<boolean>(false);

  // Game state
  const [cannonFocusActive, setCannonFocusActive] = useState<boolean>(false);
  const [cannonFocusTarget, setCannonFocusTarget] = useState<{
    name: string | null;
    hp: number;
    maxHp: number;
    faction?: string;
    level?: number;
  }>({ name: null, hp: 0, maxHp: 0, faction: "neutral", level: 1 });
  const [inWhirlpool, setInWhirlpool] = useState<boolean>(false);
  const [whirlpoolInstance, setWhirlpoolInstance] = useState<{
    entryId: string;
    class: "C1" | "C2" | "C3" | "C4" | "C5" | "C6";
    className: string;
    effect: string;
    effectModifiers: {
      armorMultiplier: number;
      shieldMultiplier: number;
      damageMultiplier: number;
      speedMultiplier: number;
      repairMultiplier: number;
      signatureMultiplier: number;
      description: string;
    };
    width: number;
    height: number;
    exitPoint: {
      tileX: number;
      tileY: number;
      worldX: number;
      worldY: number;
      stable: boolean;
    };
    playerCount: number;
    lootMultiplier: number;
    xpMultiplier: number;
  } | null>(null);
  const [whirlpoolExpiresAt, setWhirlpoolExpiresAt] = useState<
    number | undefined
  >(undefined);
  const [inRift, setInRift] = useState<boolean>(false);
  const [currentRiftId, setCurrentRiftId] = useState<string>("core");
  const [riftMetadata, setRiftMetadata] = useState<{
    id: string;
    name: string;
    layer: number;
    anomalyLevel: number;
    theme: string | null;
    jumpsToCore?: number;
  } | null>(null);
  const [inSanctum, setInSanctum] = useState<boolean>(false);
  const [currentSanctumId, setCurrentSanctumId] = useState<string | null>(null);
  const [sanctumMetadata, setSanctumMetadata] = useState<{
    id: string;
    number: number;
    type: "survival" | "preservation" | "experiment";
    grade: "A" | "B" | "C" | "D";
  } | null>(null);
  const [riftStorms, setRiftStorms] = useState<RiftStormData[]>([]);
  const [pirateRaids, setPirateRaids] = useState<PirateRaidData[]>([]);
  const [seaMonsters, setSeaMonsters] = useState<
    Array<{ id: string; type: string; x: number; y: number }>
  >([]);
  const [riftScars, setRiftScars] = useState<
    Array<{ id: string; x: number; y: number; name: string }>
  >([]);
  const [inRiftStorm, setInRiftStorm] = useState<boolean>(false);
  const [riftStormBonus, setRiftStormBonus] = useState<{
    loot: number;
    xp: number;
  } | null>(null);
  const [hasEssenceEquipped, setHasEssenceEquipped] = useState<boolean>(false);
  const [currentRiftStormId, setCurrentRiftStormId] = useState<string>("");
  const [currentRiftStormTier, setCurrentRiftStormTier] = useState<string>("");
  const [currentRiftStormLootMultiplier, setCurrentRiftStormLootMultiplier] =
    useState<number>(1.0);
  const [currentRiftStormXpMultiplier, setCurrentRiftStormXpMultiplier] =
    useState<number>(1.0);

  // Faction reputation state
  const [factionReputation, setFactionReputation] = useState<{
    navy: number;
    pirate: number;
    merchant: number;
    smuggler: number;
  }>({ navy: 0, pirate: 0, merchant: 100, smuggler: 0 });
  const [factionTiers, setFactionTiers] = useState<{
    navy: { tier: string; name: string; color: string };
    pirate: { tier: string; name: string; color: string };
    merchant: { tier: string; name: string; color: string };
    smuggler: { tier: string; name: string; color: string };
  }>({
    navy: { tier: "neutral", name: "Neutral", color: "#95a5a6" },
    pirate: { tier: "neutral", name: "Neutral", color: "#95a5a6" },
    merchant: { tier: "neutral", name: "Neutral", color: "#95a5a6" },
    smuggler: { tier: "neutral", name: "Neutral", color: "#95a5a6" },
  });
  const [factionDailyGains, setFactionDailyGains] = useState<{
    navy: number;
    pirate: number;
    merchant: number;
    smuggler: number;
  }>({ navy: 0, pirate: 0, merchant: 0, smuggler: 0 });
  const [factionDailyCaps, setFactionDailyCaps] = useState<{
    navy: number;
    pirate: number;
    merchant: number;
    smuggler: number;
  }>({ navy: 500, pirate: 500, merchant: 500, smuggler: 500 });

  // Settings state
  const [volume, setVolume] = useState<number>(() => {
    try {
      const saved: string | null = localStorage.getItem("masterVolume");
      if (saved === null) return 100;
      const parsed = Number(saved);
      if (!Number.isFinite(parsed)) return 100;
      return Math.max(0, Math.min(100, Math.round(parsed * 100)));
    } catch {
      return 100;
    }
  });
  const [showPlayerIdentityUI, setShowPlayerIdentityUI] = useState<boolean>(
    () => {
      try {
        const saved = localStorage.getItem("showPlayerIdentityUI");
        return saved === null ? true : saved === "true";
      } catch {
        return true;
      }
    },
  );
  const [showMyNameLabel, setShowMyNameLabel] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("showMyNameLabel");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  });
  const [showMyFlag, setShowMyFlag] = useState<boolean>(() => {
    try {
      const saved = localStorage.getItem("showMyFlag");
      return saved === null ? true : saved === "true";
    } catch {
      return true;
    }
  });
  const [showOtherPlayerNames, setShowOtherPlayerNames] = useState<boolean>(
    () => {
      try {
        const saved = localStorage.getItem("showOtherPlayerNames");
        return saved === null ? true : saved === "true";
      } catch {
        return true;
      }
    },
  );
  const [showOtherPlayerFlags, setShowOtherPlayerFlags] = useState<boolean>(
    () => {
      try {
        const saved = localStorage.getItem("showOtherPlayerFlags");
        return saved === null ? true : saved === "true";
      } catch {
        return true;
      }
    },
  );
  const [backgroundMusicEnabled, setBackgroundMusicEnabled] = useState<boolean>(
    () => {
      try {
        const saved: string | null = localStorage.getItem(
          "backgroundMusicEnabled",
        );
        if (saved === null) return isMusicEnabled();
        return saved === "true";
      } catch {
        return true;
      }
    },
  );
  const [sfxVolume, setSfxVolume] = useState<number>(() => {
    try {
      const saved: string | null = localStorage.getItem("sfxVolume");
      if (saved === null) return 100;
      const parsed = Number(saved);
      if (!Number.isFinite(parsed)) return 100;
      return Math.max(0, Math.min(100, Math.round(parsed * 100)));
    } catch {
      return 100;
    }
  });
  const [musicVolume, setMusicVolume] = useState<number>(() => {
    try {
      const saved: string | null = localStorage.getItem("musicVolume");
      if (saved === null) return 100;
      const parsed = Number(saved);
      if (!Number.isFinite(parsed)) return 100;
      return Math.max(0, Math.min(100, Math.round(parsed * 100)));
    } catch {
      return 100;
    }
  });
  const [cameraZoom, setCameraZoom] = useState<number>(() => {
    try {
      const saved: string | null = localStorage.getItem("cameraZoom");
      if (saved === null) return 150; // Default to 1.5x zoom
      const parsed = Number(saved);
      if (!Number.isFinite(parsed)) return 150;
      return Math.max(40, Math.min(250, Math.round(parsed))); // Range: 40-250
    } catch {
      return 150;
    }
  });
  const [worldEventNotificationsEnabled, setWorldEventNotificationsEnabled] =
    useState<boolean>(() => {
      try {
        const saved: string | null = localStorage.getItem(
          "worldEventNotificationsEnabled",
        );
        if (saved === null) return true;
        return saved === "true";
      } catch {
        return true;
      }
    });
  const [serverFeedbackEnabled, setServerFeedbackEnabled] = useState<boolean>(
    () => {
      try {
        const saved: string | null = localStorage.getItem(
          "serverFeedbackEnabled",
        );
        if (saved === null) return false;
        return saved === "true";
      } catch {
        return false;
      }
    },
  );

  // Persist notification settings to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(
        "worldEventNotificationsEnabled",
        String(worldEventNotificationsEnabled),
      );
    } catch {
      // ignore
    }
  }, [worldEventNotificationsEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "serverFeedbackEnabled",
        String(serverFeedbackEnabled),
      );
    } catch {
      // ignore
    }
  }, [serverFeedbackEnabled]);
  const [guestOnrampDismissed, setGuestOnrampDismissed] = useState<boolean>(
    () => {
      try {
        return localStorage.getItem("guestOnrampDismissed") === "true";
      } catch {
        return false;
      }
    },
  );

  // Quest state
  const [starterVoyageMinimized, setStarterVoyageMinimized] =
    useState<boolean>(false);
  const [starterVoyageHidden, setStarterVoyageHidden] =
    useState<boolean>(false);
  const [chatMessage, setChatMessage] = useState<string>("");

  // Ship tooltip state
  const [shipTooltip, setShipTooltip] = useState<{
    visible: boolean;
    x: number;
    y: number;
    shipData: any;
    shipPosition: { x: number; y: number };
    portraitIndex?: number;
  } | null>(null);

  // Other state
  const [apiBaseUrl, setApiBaseUrl] = useState<string>(
    process.env.REACT_APP_API_URL || "https://api.grand.lat",
  );
  const [researchCanDock] = useState<boolean>(false);
  const [researchIsDocked] = useState<boolean>(false);
  const [researchIsDockedHere] = useState<boolean>(false);

  // Refs
  const isDockedRef = useRef<boolean>(false);
  const dockedIslandIdRef = useRef<string | null>(null);
  const researchIslandIdRef = useRef<string | null>(null);
  const researchPanelOpenRef = useRef<boolean>(false);

  // Sync refs with state
  useEffect(() => {
    isDockedRef.current = isDocked;
  }, [isDocked]);

  useEffect(() => {
    dockedIslandIdRef.current = dockedIslandId;
  }, [dockedIslandId]);

  useEffect(() => {
    researchIslandIdRef.current = researchIslandId;
  }, [researchIslandId]);

  useEffect(() => {
    researchPanelOpenRef.current = researchPanelOpen;
  }, [researchPanelOpen]);

  useEffect(() => {
    setMusicEnabled(backgroundMusicEnabled);
    try {
      localStorage.setItem(
        "backgroundMusicEnabled",
        String(backgroundMusicEnabled),
      );
    } catch {
      // ignore
    }
  }, [backgroundMusicEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "showPlayerIdentityUI",
        String(showPlayerIdentityUI),
      );
    } catch {
      // ignore
    }
  }, [showPlayerIdentityUI]);

  useEffect(() => {
    try {
      localStorage.setItem("showMyNameLabel", String(showMyNameLabel));
    } catch {
      // ignore
    }
  }, [showMyNameLabel]);

  useEffect(() => {
    try {
      localStorage.setItem("showMyFlag", String(showMyFlag));
    } catch {
      // ignore
    }
  }, [showMyFlag]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "showOtherPlayerNames",
        String(showOtherPlayerNames),
      );
    } catch {
      // ignore
    }
  }, [showOtherPlayerNames]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "showOtherPlayerFlags",
        String(showOtherPlayerFlags),
      );
    } catch {
      // ignore
    }
  }, [showOtherPlayerFlags]);

  // Toast helpers
  useEffect(() => {
    try {
      localStorage.setItem(
        "showPlayerIdentityUI",
        String(showPlayerIdentityUI),
      );
    } catch {
      // ignore
    }
  }, [showPlayerIdentityUI]);

  useEffect(() => {
    try {
      localStorage.setItem("showMyNameLabel", String(showMyNameLabel));
    } catch {
      // ignore
    }
  }, [showMyNameLabel]);

  useEffect(() => {
    try {
      localStorage.setItem("showMyFlag", String(showMyFlag));
    } catch {
      // ignore
    }
  }, [showMyFlag]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "showOtherPlayerNames",
        String(showOtherPlayerNames),
      );
    } catch {
      // ignore
    }
  }, [showOtherPlayerNames]);

  useEffect(() => {
    try {
      localStorage.setItem(
        "showOtherPlayerFlags",
        String(showOtherPlayerFlags),
      );
    } catch {
      // ignore
    }
  }, [showOtherPlayerFlags]);

  // Toast helpers
  const removeToast = useCallback((id: string): void => {
    setToastQueue((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const enqueueToast = useCallback(
    (text: string, type: "error" | "warning" | "success" = "error"): void => {
      setToastQueue((prev) => {
        // Coalesce identical text+type already in queue
        if (prev.some((t) => t.text === text && t.type === type)) {
          return prev;
        }
        const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        // Auto-dismiss after 4s
        setTimeout(() => removeToast(id), 4000);
        return [...prev, { id, text, type }];
      });
    },
    [removeToast],
  );

  // World event helpers
  const removeWorldEvent = useCallback((eventId: string): void => {
    setWorldEvents((prev) => prev.filter((e) => e.id !== eventId));
  }, []);

  const addWorldEvent = useCallback(
    (event: Omit<WorldEvent, "id" | "timestamp">): void => {
      const id = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
      const newEvent: WorldEvent = {
        ...event,
        id,
        timestamp: Date.now(),
      };
      setWorldEvents((prev) => [...prev, newEvent]);

      // Auto-dismiss after 8 seconds if no expiration set
      if (!event.expiresAt) {
        setTimeout(() => removeWorldEvent(id), 8000);
      }
    },
    [removeWorldEvent],
  );

  // Combat cooldown helpers
  const addCombatCooldown = useCallback((cooldown: Cooldown): void => {
    setCombatCooldowns((prev) => {
      // Remove if already exists
      const filtered = prev.filter((c) => c.id !== cooldown.id);
      return [...filtered, cooldown];
    });
  }, []);

  // Remove expired cooldowns periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setCombatCooldowns((prev) => {
        const now = Date.now();
        return prev.filter((c) => c.expiresAt > now);
      });
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Close docked overlays helper
  const closeDockedOverlays = useCallback((): void => {
    setShopOpen(false);
    setShipyardOpen(false);
    setCourierPanelOpen(false);
    setTavernOpen(false);
    setResearchPanelOpen(false);
  }, []);

  return {
    // Player state
    playerId,
    setPlayerId,
    shipCount,
    setShipCount,
    position,
    setPosition,
    currentRegion,
    setCurrentRegion,
    tilePos,
    setTilePos,
    chunkPos,
    setChunkPos,
    securityTier,
    setSecurityTier,
    playerHp,
    setPlayerHp,
    playerSP,
    setPlayerSP,
    inCombat,
    setInCombat,
    pieces,
    setPieces,
    xp,
    setXp,
    level,
    setLevel,
    infamy,
    setInfamy,
    connected,
    setConnected,
    unlockedBlueprints,
    setUnlockedBlueprints,
    shipId,
    setShipId,
    shipType,
    setShipType,

    // Panel states
    inventoryOpen,
    setInventoryOpen,
    shopOpen,
    setShopOpen,
    currentTradingPost,
    setCurrentTradingPost,
    shipyardOpen,
    setShipyardOpen,
    currentShipyard,
    setCurrentShipyard,
    fleetPanelOpen,
    setFleetPanelOpen,
    expressionPanelOpen,
    setExpressionPanelOpen,
    debugPanelOpen,
    setDebugPanelOpen,
    moderatorPanelOpen,
    setModeratorPanelOpen,
    researchPanelOpen,
    setResearchPanelOpen,
    researchIslandId,
    setResearchIslandId,
    researchIslandPosition,
    setResearchIslandPosition,
    researchRefreshNonce,
    setResearchRefreshNonce,
    tavernOpen,
    setTavernOpen,
    bankOpen,
    setBankOpen,
    questLogOpen,
    setQuestLogOpen,
    questsPanelOpen,
    setQuestsPanelOpen,
    inboxOpen,
    setInboxOpen,
    ambientTradeOpen,
    setAmbientTradeOpen,
    feedbackModalOpen,
    setFeedbackModalOpen,
    bottleModalOpen,
    setBottleModalOpen,
    shipInvestigationOpen,
    setShipInvestigationOpen,
    fishingResearchOpen,
    setFishingResearchOpen,
    resourceResearchOpen,
    setResourceResearchOpen,
    lootOpen,
    setLootOpen,
    worldHistoryOpen,
    setWorldHistoryOpen,
    infamyInfoOpen,
    setInfamyInfoOpen,
    reputationOpen,
    setReputationOpen,
    chatOpen,
    setChatOpen,
    skillsOpen,
    setSkillsOpen,
    fittingOpen,
    setFittingOpen,
    craftingOpen,
    setCraftingOpen,
    cookingOpen,
    setCookingOpen,
    smithingOpen,
    setSmithingOpen,
    alchemyOpen,
    setAlchemyOpen,
    constructionHotbarActive,
    setConstructionHotbarActive,
    currentConstructionKitId,
    setCurrentConstructionKitId,
    territoryClaimOpen,
    setTerritoryClaimOpen,
    siegePanelOpen,
    setSiegePanelOpen,
    territoryManagementOpen,
    setTerritoryManagementOpen,
    whirlpoolResearchOpen,
    setWhirlpoolResearchOpen,
    riftScarData,
    setRiftScarData,
    signaturePanelOpen,
    setSignaturePanelOpen,
    optionsOpen,
    setOptionsOpen,
    referralPanelOpen,
    setReferralPanelOpen,
    dailyRewardsPanelOpen,
    setDailyRewardsPanelOpen,
    courierPanelOpen,
    setCourierPanelOpen,
    riftRelicPanelOpen,
    setRiftRelicPanelOpen,
    dockServicesPanelOpen,
    setDockServicesPanelOpen,
    currentShipHeat,
    setCurrentShipHeat,
    maxShipHeat,
    setMaxShipHeat,

    // Docking state
    isDocked,
    setIsDocked,
    dockedIslandId,
    setDockedIslandId,
    dockedIslandName,
    setDockedIslandName,
    dockedIslandHasShop,
    setDockedIslandHasShop,
    dockedIslandHasShipyard,
    setDockedIslandHasShipyard,
    dockedIslandIsHarborOffice,
    setDockedIslandIsHarborOffice,
    dockedIslandHasTavern,
    setDockedIslandHasTavern,
    dockedIslandHasCourier,
    setDockedIslandHasCourier,
    dockedIslandHasDockServices,
    setDockedIslandHasDockServices,
    dockedIslandIsPermanent,
    setDockedIslandIsPermanent,
    dockPrompt,
    setDockPrompt,
    dockedIslandNpcs,
    setDockedIslandNpcs,

    // UI state
    toastQueue,
    setToastQueue,
    worldEvents,
    setWorldEvents,
    combatCooldowns,
    setCombatCooldowns,
    kickMessage,
    setKickMessage,
    isTypingInInput,
    setIsTypingInInput,
    removeToast,
    enqueueToast,
    removeWorldEvent,
    addWorldEvent,
    addCombatCooldown,

    // Research/Interaction state
    fishingSpotId,
    setFishingSpotId,
    fishingSpotType,
    setFishingSpotType,
    fishingSpotPosition,
    setFishingSpotPosition,
    resourceNodeId,
    setResourceNodeId,
    resourceNodeType,
    setResourceNodeType,
    resourceNodePosition,
    setResourceNodePosition,
    lootContainerId,
    setLootContainerId,
    whirlpoolId,
    setWhirlpoolId,
    whirlpoolPosition,
    setWhirlpoolPosition,
    siegeIslandId,
    setSiegeIslandId,
    siegeIslandName,
    setSiegeIslandName,
    managementIslandId,
    setManagementIslandId,
    managementIslandName,
    setManagementIslandName,
    managementIsOwner,
    setManagementIsOwner,
    territoryStatusOpen,
    setTerritoryStatusOpen,
    statusIslandId,
    setStatusIslandId,
    statusIslandName,
    setStatusIslandName,
    claimIslandData,
    setClaimIslandData,
    investigatedShip,
    setInvestigatedShip,
    bottleContent,
    setBottleContent,
    npcDialogueOpen,
    setNpcDialogueOpen,
    npcDialogueData,
    setNpcDialogueData,
    ambientTradeData,
    setAmbientTradeData,
    harvestProgress,
    setHarvestProgress,
    harvestReward,
    setHarvestReward,

    // Loading overlay state
    loadingOverlayVisible,
    setLoadingOverlayVisible,
    loadingOverlayMessage,
    setLoadingOverlayMessage,
    isInstanceEntry,
    setIsInstanceEntry,

    // Game state
    cannonFocusActive,
    setCannonFocusActive,
    cannonFocusTarget,
    setCannonFocusTarget,
    inWhirlpool,
    setInWhirlpool,
    whirlpoolInstance,
    setWhirlpoolInstance,
    whirlpoolExpiresAt,
    setWhirlpoolExpiresAt,
    inRift,
    setInRift,
    currentRiftId,
    setCurrentRiftId,
    riftMetadata,
    setRiftMetadata,
    inSanctum,
    setInSanctum,
    currentSanctumId,
    setCurrentSanctumId,
    sanctumMetadata,
    setSanctumMetadata,
    riftStorms,
    setRiftStorms,
    pirateRaids,
    setPirateRaids,
    seaMonsters,
    setSeaMonsters,
    riftScars,
    setRiftScars,
    inRiftStorm,
    setInRiftStorm,
    riftStormBonus,
    setRiftStormBonus,
    hasEssenceEquipped,
    setHasEssenceEquipped,
    currentRiftStormId,
    setCurrentRiftStormId,
    currentRiftStormTier,
    setCurrentRiftStormTier,
    currentRiftStormLootMultiplier,
    setCurrentRiftStormLootMultiplier,
    currentRiftStormXpMultiplier,
    setCurrentRiftStormXpMultiplier,

    // Faction reputation state
    factionReputation,
    setFactionReputation,
    factionTiers,
    setFactionTiers,
    factionDailyGains,
    setFactionDailyGains,
    factionDailyCaps,
    setFactionDailyCaps,

    // Settings state
    volume,
    setVolume,
    cameraZoom,
    setCameraZoom,
    showPlayerIdentityUI,
    setShowPlayerIdentityUI,
    showMyNameLabel,
    setShowMyNameLabel,
    showMyFlag,
    setShowMyFlag,
    showOtherPlayerNames,
    setShowOtherPlayerNames,
    showOtherPlayerFlags,
    setShowOtherPlayerFlags,
    backgroundMusicEnabled,
    setBackgroundMusicEnabled,
    sfxVolume,
    setSfxVolume,
    musicVolume,
    setMusicVolume,
    worldEventNotificationsEnabled,
    setWorldEventNotificationsEnabled,
    serverFeedbackEnabled,
    setServerFeedbackEnabled,
    guestOnrampDismissed,
    setGuestOnrampDismissed,

    // Quest state
    starterVoyageMinimized,
    setStarterVoyageMinimized,
    starterVoyageHidden,
    setStarterVoyageHidden,
    questJournalOpen,
    setQuestJournalOpen,
    selectedQuestForJournal,
    setSelectedQuestForJournal,
    trackedQuestId,
    setTrackedQuestId,
    chatMessage,
    setChatMessage,

    // Other state
    apiBaseUrl,
    setApiBaseUrl,
    researchCanDock,
    researchIsDocked,
    researchIsDockedHere,
    socketForHotbar,
    setSocketForHotbar,
    inventoryItems,
    setInventoryItems,
    hotbarVisible,
    setHotbarVisible,
    isDisconnected,
    setIsDisconnected,
    reconnectAttempt,
    setReconnectAttempt,
    duplicateConnectionError,
    setDuplicateConnectionError,
    currentWeather,
    setCurrentWeather,
    shipTooltip,
    setShipTooltip,

    // Refs
    isDockedRef,
    dockedIslandIdRef,
    researchIslandIdRef,
    researchPanelOpenRef,

    // Helpers
    closeDockedOverlays,
  };
}
